import classnames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { DevicesCategory, RequiredMediaSource, Session } from '@app/api';
import { Theme } from '@app/theme';
import { IconChip, LabeledSectionWithChipsList } from '@app/core';

export const DEVICE_CATEGORY_ICONS = {
  COMPUTER: 'ri-computer-line',
  TABLET: 'ri-tablet-line',
  SMARTPHONE: 'ri-smartphone-line',
};

const REQUIRED_MEDIA_ICONS = {
  VIDEO: 'ri-webcam-line',
  AUDIO: 'ri-mic-line',
  SCREEN_SHARING: 'ri-cast-line',
};

interface Props {
  session: Session;
  className?: string;
  dynamicDevices?: boolean;
  singleDeviceWarning?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
  },
}));

export const TechnicalRequirements: React.FC<Props> = ({
  session,
  className,
  dynamicDevices = false,
  singleDeviceWarning = false,
}) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const devicesChipsData: Array<{
    label: string;
    icon: string;
  }> = useMemo(() => {
    let devices: DevicesCategory[] = [];
    if (dynamicDevices) {
      devices = session.availableDevicesCategories || [];
    } else {
      devices = session.allowedDevicesCategories || [];
    }

    return devices.map((device: DevicesCategory) => {
      return {
        label: t(
          `booking.technicalRequirements.allowedDevicesCategories.${device}`,
        ),
        icon: DEVICE_CATEGORY_ICONS[device],
      };
    });
  }, [
    dynamicDevices,
    session.allowedDevicesCategories,
    session.availableDevicesCategories,
    t,
  ]);

  const mediaSourceChipsData: Array<{
    label: string;
    icon: string;
  }> = useMemo(
    () =>
      session.requiredMediaSource
        ? session.requiredMediaSource.map((device: RequiredMediaSource) => {
            return {
              label: t(
                `booking.technicalRequirements.requiredMediaSource.${device}`,
              ),
              icon: REQUIRED_MEDIA_ICONS[device],
            };
          })
        : [],
    [session.requiredMediaSource, t],
  );
  return (
    <div className={classnames(classes.container, className)}>
      {devicesChipsData.length > 0 && (
        <LabeledSectionWithChipsList
          label={t('booking.technicalRequirements.allowedDevicesLabel')}
          chipsData={devicesChipsData}
        >
          {singleDeviceWarning && devicesChipsData.length === 1 ? (
            <IconChip icon="ri-error-warning-line" color="red">
              <span>
                {t(
                  'booking.technicalRequirements.singleAvailableDeviceWarning',
                )}
              </span>
            </IconChip>
          ) : undefined}
        </LabeledSectionWithChipsList>
      )}
      {mediaSourceChipsData.length > 0 && (
        <LabeledSectionWithChipsList
          label={t('booking.technicalRequirements.requiredMediaSourceLabel')}
          chipsData={mediaSourceChipsData}
        />
      )}
    </div>
  );
};
